import { useMemo, useState } from "react";
import { Rule } from "../../../interfaces/Rule";
import { Tag } from "../../../interfaces/Tag";
import { ActionGroup } from "../../../interfaces/ActionButton";
import { formatMessage } from "../../../../utils/helpers/LanguageHelper";
import { deleteRuleFromCustomXml } from "../../../../utils/helpers/CustomXmlCoRuleHelper";

export interface RulesPanelProps {
  documentId: string;
  rules?: Rule[];
  tags?: Tag[];
  updateRules?: (rules: Rule[]) => void;
  /**
   * Fired with a single ruleId when the inline per-rule "Run" action is clicked. `RulesPanel.tsx`
   * wires this to `runSelected([ruleId])` — the same immediate-streaming-panel entry point the
   * multi-select "Run"/"Run all" footer button uses.
   */
  onRunRule?: (ruleId: string) => void;
}

export interface RulesPanelResult {
  rules: Rule[];
  filteredRules: Rule[];
  search: string;
  expanded: string[];
  showAddRulesModal: boolean;
  setShowAddRulesModal: (s: boolean) => void;
  ruleToEdit: Rule | null;
  setRuleToEdit: (r: Rule | null) => void;
  deleteConfirm: boolean;
  ruleToDelete: Rule | null;
  actionGroups: ActionGroup[];
  handleFilter: (q: string) => void;
  handleAccordionToggle: (event: any, data: any) => void;
  confirmDelete: () => Promise<void>;
  cancelDelete: () => void;
}

export function useRulesPanel(props: RulesPanelProps): RulesPanelResult {
  const { rules = [], updateRules } = props;

  const [search, setSearch] = useState<string>("");
  const [expanded, setExpanded] = useState<string[]>([]);
  const [showAddRulesModal, setShowAddRulesModal] = useState<boolean>(false);

  const [ruleToEdit, setRuleToEdit] = useState<Rule | null>(null);

  const [deleteConfirm, setDeleteConfirm] = useState<boolean>(false);
  const [ruleToDelete, setRuleToDelete] = useState<Rule | null>(null);

  const filteredRules = useMemo(() => {
    if (!search) return rules;
    const q = search.toLowerCase();
    return rules.filter((r) => (r.title ?? "").toLowerCase().includes(q) || (r.script ?? "").toLowerCase().includes(q));
  }, [rules, search]);

  const handleAccordionToggle = (_: any, data: any) => {
    const openItems = Array.isArray(data.openItems) ? data.openItems : [data.openItems].filter(Boolean);
    setExpanded(openItems);
  };

  const handleFilter = (query: string) => {
    const lowerQuery = query.toLowerCase();
    setSearch(lowerQuery);
    setExpanded((prev) => prev);
  };

  const confirmDelete = async () => {
    if (!ruleToDelete) return;
    await deleteRuleFromCustomXml(ruleToDelete.id);
    updateRules?.(rules.filter((r) => r.id !== ruleToDelete.id));
    setRuleToDelete(null);
    setDeleteConfirm(false);
  };

  const cancelDelete = () => {
    setRuleToDelete(null);
    setDeleteConfirm(false);
  };

  /* ------------------- ACTION BUTTONS ------------------- */
  const actionGroups: ActionGroup[] = useMemo(
    () => [
      {
        key: "run",
        buttons: [
          {
            key: "run",
            title: formatMessage("cof.validation.button.runrule") || "Run",
            icon: "k-icon k-i-play",
            style: { color: "var(--color-blue)", cursor: "pointer" },
            onClick: (ruleId: string) => {
              // Delegates to RulesPanel.tsx's runSelected([ruleId]) — see onRunRule's doc comment
              // above.
              props.onRunRule?.(ruleId);
            },
          },
        ],
      },
      {
        key: "main",
        buttons: [
          {
            key: "edit",
            title: formatMessage("cof.base.edit"),
            icon: "k-icon k-i-edit",
            style: { color: "var(--color-blue)", cursor: "pointer" },
            onClick: (ruleId: string) => {
              const r = rules.find((x) => x.id === ruleId) || null;
              if (!r) return;
              setRuleToEdit(r);
              setShowAddRulesModal(true);
            },
          },
          {
            key: "delete",
            title: formatMessage("cof.base.delete"),
            icon: "k-icon k-i-delete",
            style: { color: "var(--color-red)", cursor: "pointer" },
            onClick: (ruleId: string) => {
              const r = rules.find((x) => x.id === ruleId) || null;
              if (!r) return;
              setRuleToDelete(r);
              setDeleteConfirm(true);
            },
          },
        ],
      },
    ],
    [rules, props.onRunRule]
  );

  return {
    rules,
    filteredRules,
    search,
    expanded,
    showAddRulesModal,
    setShowAddRulesModal,
    ruleToEdit,
    setRuleToEdit,
    deleteConfirm,
    ruleToDelete,
    actionGroups,
    handleFilter,
    handleAccordionToggle,
    confirmDelete,
    cancelDelete,
  };
}
